import { useQuery } from '@tanstack/react-query';

import { getQueryKey } from '@/api/utils/getQueryKey';
import { getResolvedEndpoint } from '@/api/utils/getResolvedEndpoint';
import { handleApiCall } from '@/api/utils/handleApiCall';
import { useAuth } from '@/context/AuthProvider';

interface BillMetadata {
  provider?: string;
  bill_type?: string;
  amount_due?: number;
  due_date?: string;
  bill_date?: string;
  account_number?: string;
  billing_period?: {
    start: string;
    end: string;
  };
  property_address?: string;
  user?: {
    first_name?: string;
    last_name?: string;
    email?: string | null;
    phone?: string | null;
  };
  payment_status?: string;
  autopay_enabled?: boolean;
  electric_details?: {
    total: number;
    supply: number;
    delivery: number;
    ev_credit?: number;
  };
  gas_details?: {
    total: number;
    supply: number;
    delivery: number;
  };
  total_usage?: {
    gas_therms?: number;
    electric_kwh?: number;
  };
}

interface ExtractedDocumentData {
  success: boolean;
  summary?: string;
  document_type?: string;
  custom_metadata?: BillMetadata;
  id?: number;
  ocred?: boolean;
  checksum?: string;
  file_url?: string;
  data_type?: string;
  user_uuid?: string | null;
  created_at?: string;
  updated_at?: string;
  website_id?: number | null;
}

export interface DocumentExtractionData {
  document?: ExtractedDocumentData;
  success?: boolean;
}

export interface DocumentExtractionResponse {
  data: DocumentExtractionData;
  success: boolean;
}

const METHOD = 'GET';
const ENDPOINT = 'document/:id/extract';
export const QUERY_KEY = (id: string) => getQueryKey(METHOD, ENDPOINT, { id });

/**
 * Hook to fetch document extraction data from the API
 * @param id The ID of the document to extract data from
 * @param enabled Whether the query should be enabled (defaults to true)
 * @returns Query result with document extraction data
 */
export const useDocumentExtract = (id: string, enabled = true) => {
  const { session } = useAuth();

  return useQuery<DocumentExtractionData>({
    queryKey: QUERY_KEY(id),
    queryFn: async () => {
      const response = await handleApiCall<DocumentExtractionResponse>(
        session,
        METHOD,
        getResolvedEndpoint(ENDPOINT, {
          id: id.toString(),
        })
      );

      return response.data;
    },
    enabled: enabled && !!session?.access_token,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: false,
  });
};
